import { useCallback, useMemo, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDeleteSimProfile, useSimProfiles } from "@/hooks/useSims";
import {
  PROVIDER_LABELS,
  countryName,
  profileCountries,
  type SimCard,
  type SimProfile,
  type SimProvider,
} from "@/lib/sims";
import { ProviderBadge } from "./SimBadges";
import { SimProfileDialog } from "./SimProfileDialog";

/** Profiles as a matrix: one row per profile, one column per country. */
export function SimProfilesPanel({
  sims,
  onShowSims,
}: {
  sims: SimCard[];
  /** Show the SIMs of a profile in the SIM list. */
  onShowSims: (profileId: string) => void;
}) {
  const { data: profiles, isLoading } = useSimProfiles();
  const deleteProfile = useDeleteSimProfile();
  const [editing, setEditing] = useState<SimProfile | null | undefined>(
    undefined,
  );
  const [deleting, setDeleting] = useState<SimProfile | null>(null);

  const countries = useMemo(() => profileCountries(profiles), [profiles]);
  const ratePlans = useMemo(() => {
    const byProvider = new Map<SimProvider, Set<string>>();
    for (const sim of sims) {
      if (!sim.ratePlan) continue;
      const set = byProvider.get(sim.provider) ?? new Set<string>();
      set.add(sim.ratePlan);
      byProvider.set(sim.provider, set);
    }
    return new Map(
      Array.from(byProvider, ([provider, set]) => [provider, Array.from(set)]),
    );
  }, [sims]);
  const simCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const sim of sims) {
      if (sim.profileID)
        counts.set(sim.profileID, (counts.get(sim.profileID) ?? 0) + 1);
    }
    return counts;
  }, [sims]);
  const sorted = useMemo(
    () =>
      [...profiles].sort(
        (a, b) =>
          PROVIDER_LABELS[a.provider].localeCompare(
            PROVIDER_LABELS[b.provider],
          ) ||
          Number(b.isOpen) - Number(a.isOpen) ||
          a.name.localeCompare(b.name, undefined, { numeric: true }),
      ),
    [profiles],
  );
  const closeEditor = useCallback(
    (open: boolean) => !open && setEditing(undefined),
    [],
  );

  return (
    <div className="space-y-3" data-testid="sim-profiles-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Preferred network per country. SIMs get a profile from their rate
          plan; SIMs on other plans can be given one by hand.
        </p>
        <Button
          size="sm"
          onClick={() => setEditing(null)}
          data-testid="add-sim-profile"
        >
          <Plus className="mr-1.5 h-4 w-4" /> Add profile
        </Button>
      </div>
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Provider</TableHead>
              <TableHead>Profile</TableHead>
              {countries.map((code) => (
                <TableHead key={code} className="whitespace-nowrap">
                  {countryName(code)}
                </TableHead>
              ))}
              <TableHead>Rate plan</TableHead>
              <TableHead className="text-right">SIMs</TableHead>
              <TableHead className="w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && profiles.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={countries.length + 5}
                  className="py-8 text-center text-muted-foreground"
                >
                  Loading profiles…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && profiles.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={countries.length + 5}
                  className="py-8 text-center text-muted-foreground"
                >
                  No profiles yet.
                </TableCell>
              </TableRow>
            )}
            {sorted.map((profile) => {
              const byCountry = new Map(
                profile.networks.map((n) => [n.countryCode, n]),
              );
              const count = simCounts.get(profile.id) ?? 0;
              return (
                <TableRow
                  key={profile.id}
                  data-testid={`sim-profile-row-${profile.provider}-${profile.name}`}
                >
                  <TableCell>
                    <ProviderBadge provider={profile.provider} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap font-medium">
                    {profile.name}
                    {profile.isOpen && (
                      <Badge variant="secondary" className="ml-2">
                        Open
                      </Badge>
                    )}
                    {profile.notes && (
                      <div className="max-w-56 whitespace-normal text-xs font-normal text-muted-foreground">
                        {profile.notes}
                      </div>
                    )}
                  </TableCell>
                  {countries.map((code) => {
                    const entry = byCountry.get(code);
                    return (
                      <TableCell
                        key={code}
                        className="whitespace-nowrap"
                        title={entry?.note ?? undefined}
                      >
                        {entry && entry.networks.length > 0 ? (
                          entry.networks.join(" › ")
                        ) : (
                          <span className="text-muted-foreground">
                            {profile.isOpen ? "Any" : "–"}
                          </span>
                        )}
                        {entry?.note && (
                          <span className="ml-1 text-muted-foreground">*</span>
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell className="whitespace-nowrap font-mono text-xs">
                    {profile.ratePlan ?? (
                      <span className="font-sans text-muted-foreground">
                        By hand
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {count > 0 ? (
                      <button
                        type="button"
                        className="hover:underline"
                        onClick={() => onShowSims(profile.id)}
                      >
                        {count}
                      </button>
                    ) : (
                      0
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      aria-label={`Edit ${profile.name}`}
                      onClick={() => setEditing(profile)}
                      data-testid="edit-sim-profile"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      aria-label={`Delete ${profile.name}`}
                      onClick={() => setDeleting(profile)}
                      data-testid="delete-sim-profile"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <SimProfileDialog
        open={editing !== undefined}
        onOpenChange={closeEditor}
        profile={editing ?? null}
        ratePlans={ratePlans}
        defaultCountries={countries}
      />
      <AlertDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              SIMs that got this profile by hand will have no profile. SIMs are
              not changed at the carrier.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleting && deleteProfile.mutate(deleting.id)}
              data-testid="confirm-delete-sim-profile"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
